import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { webNavigate } from "../../services/navigate.service";
import BackIcon from "../../images/BackIcon";
import LoadingV2 from "../../components/LoadingV2";
import { DropdownSearch } from "../../components/DropdownSearch";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { useResponsive } from "../../hook/useResponsive";
import { getLocations } from "../../services/locations.services";
import TagStatus from "../../components/TagStatus";
import {
  BORDER_SOFT,
  ConfirmSheet,
  Field,
  FieldLabel,
  StickyFooter,
  SAFE_BOTTOM,
  STICKY_FOOTER_HEIGHT,
  TABBAR_OFFSET,
  TAP,
  TEAL,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
} from "../../components/mobile";
import {
  FormGrid,
  FormGridFull,
  PickerField,
  StatusChoice,
  Stepper,
  statusSegments,
  tableLoadHint,
  unresolvedTableOption,
  useLocationTables,
} from "./reservationShared";
import {
  getTableReservationById,
  RESERVATION_SOURCE_LABELS,
  RESERVATION_STATUS_LABELS,
  ReservationSource,
  ReservationStatus,
  TableReservation,
  updateTableReservation,
  UpdateTableReservationPayload,
} from "../../services/tableReservations.service";

/**
 * Detail / edit page for a single `dbo.table_reservations` row.
 *
 * Top: read-only summary (reservation #, customer, reserved window, source,
 *      created at, location). These fields are never editable from the
 *      back-office — they're owned by the originating POS / integration.
 *
 * Below: editable form for the back-office-mutable fields:
 *      - status (DropdownSearch over ReservationStatus enum labels)
 *      - floorPlanDetailId (UUID input + Clear button — mapping to a
 *        floor_plan_details list is a follow-up; this v1 keeps it
 *        deliberately blunt so we can ship)
 *      - partySize (number input)
 *      - customerPhone (text)
 *      - customerEmail (text)
 *      - notes (textarea)
 *
 * Save bar at the bottom calls `updateTableReservation` and toasts on
 * success / error. Visual language matches `Reservations.tsx` —
 * `font-inter`, brand teal `#0F7679`, rounded `30px` primary buttons,
 * `1px solid #C9D7D8` secondary buttons, `LoadingV2` overlay,
 * `BackIcon` for the back link.
 */

interface LocationOption {
  id: string;
  name: string;
  locationCode?: string;
}

const formatDateTime = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const t = (language: any, key: string, fallback?: string) =>
  (language && language[key]) || fallback || key;

const buttonPrimaryStyle: React.CSSProperties = {
  borderRadius: "30px",
  color: "#FFFFFF",
  fontSize: "14px",
  fontWeight: 500,
  backgroundColor: "#0F7679",
  padding: "10px 24px",
  border: "none",
  cursor: "pointer",
};

const buttonSecondaryStyle: React.CSSProperties = {
  borderRadius: "30px",
  padding: "10px 24px",
  border: "1px solid #C9D7D8",
  color: "#222525",
  fontSize: "14px",
  fontWeight: 500,
  backgroundColor: "transparent",
  cursor: "pointer",
};

const inputStyle: React.CSSProperties = {
  height: "48px",
  width: "100%",
  border: "1px solid #EBEBED",
  borderRadius: "12px",
  padding: "12px",
  fontSize: "14px",
  outline: "none",
  background: "transparent",
  fontFamily: "Inter, sans-serif",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  height: "120px",
  resize: "vertical",
};

const labelStyle: React.CSSProperties = {
  color: "#4A5353",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  fontFamily: "Inter, sans-serif",
};

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: "Inter, sans-serif",
  fontSize: "20px",
  fontWeight: 500,
  color: "#222525",
  lineHeight: "28px",
};

const cardRowStyle = (isLast: boolean): React.CSSProperties => ({
  padding: "12px 16px",
  borderBottom: isLast ? "none" : "1px solid #EBF0F0",
});

interface FormState {
  status: ReservationStatus | null;
  floorPlanDetailId: string;
  partySize: string;
  customerPhone: string;
  customerEmail: string;
  notes: string;
}

const TableReservationsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isMobile, isTablet, isTouchLayout } = useResponsive();
  const preLanguage = localStorage.getItem("language") || "";
  const [language, setLanguage] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [reservation, setReservation] = useState<TableReservation | null>(null);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [form, setForm] = useState<FormState>({
    status: null,
    floorPlanDetailId: "",
    partySize: "",
    customerPhone: "",
    customerEmail: "",
    notes: "",
  });

  const [statusLabel, setStatusLabel] = useState<string>("");
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  /**
   * "Nothing to save", said in the page rather than only through the host's
   * toast — the toast renders in dev-core-web's provider, outside this MFE,
   * and a tap that produces nothing visible here reads as a dead button.
   */
  const [noChangeNotice, setNoChangeNotice] = useState(false);

  const [tableLabel, setTableLabel] = useState<string>("");
  const tableDropdownRef = useRef<HTMLDivElement>(null);
  const [tableDropdownOpen, setTableDropdownOpen] = useState(false);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const locationsById = useMemo(() => {
    const map: Record<string, LocationOption> = {};
    locations.forEach((l) => (map[l.id] = l));
    return map;
  }, [locations]);

  const loadReservation = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const row = await getTableReservationById(id);
      setReservation(row);
      setForm({
        status: row.status,
        floorPlanDetailId: row.floorPlanDetailId ?? "",
        partySize: String(row.partySize ?? ""),
        customerPhone: row.customerPhone ?? "",
        customerEmail: row.customerEmail ?? "",
        notes: row.notes ?? "",
      });
      setStatusLabel(
        RESERVATION_STATUS_LABELS[row.status as ReservationStatus] ?? ""
      );
    } catch (err: any) {
      showApiError(err, { language, titleFallback: t(language, "Failed to load reservation") });
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const res: any = await getLocations(undefined, undefined, 1, 200, "");
      const rows: LocationOption[] =
        res?.data?.data?.map((l: any) => ({
          id: l.id,
          locationCode: l.locationCode,
          name: l.name,
        })) ?? [];
      setLocations(rows);
    } catch {
      // Silent — location lookup is non-critical for the read-only display.
    }
  };

  useEffect(() => {
    loadReservation();
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleBack = () => {
    webNavigate(`/${preLanguage}/tableReservations`);
  };

  /**
   * Build the PATCH payload by diffing form state vs the loaded row. Empty
   * strings on nullable fields collapse to `null` (so users can clear a
   * phone/email/notes/floorPlanDetailId). Unchanged fields are omitted to
   * keep the wire payload small and to avoid spurious validation errors.
   */
  const buildPayload = (): UpdateTableReservationPayload => {
    if (!reservation) return {};
    const payload: UpdateTableReservationPayload = {};

    if (form.status !== null && form.status !== reservation.status) {
      payload.status = form.status;
    }

    const trimmedFpd = form.floorPlanDetailId.trim();
    const newFpd = trimmedFpd === "" ? null : trimmedFpd;
    if (newFpd !== reservation.floorPlanDetailId) {
      payload.floorPlanDetailId = newFpd;
    }

    const parsedPartySize = parseInt(form.partySize, 10);
    if (
      Number.isFinite(parsedPartySize) &&
      parsedPartySize !== reservation.partySize
    ) {
      payload.partySize = parsedPartySize;
    }

    const newPhone =
      form.customerPhone.trim() === "" ? null : form.customerPhone.trim();
    if (newPhone !== reservation.customerPhone) {
      payload.customerPhone = newPhone;
    }

    const newEmail =
      form.customerEmail.trim() === "" ? null : form.customerEmail.trim();
    if (newEmail !== reservation.customerEmail) {
      payload.customerEmail = newEmail;
    }

    const newNotes = form.notes === "" ? null : form.notes;
    if (newNotes !== reservation.notes) {
      payload.notes = newNotes;
    }

    return payload;
  };

  const handleSave = async () => {
    if (!id || !reservation) return;
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      setNoChangeNotice(true);
      showToast({
        message: t(language, "Nothing to save"),
        type: "warning",
      });
      return;
    }
    setNoChangeNotice(false);

    if (partySizeError) {
      if (isTouchLayout) {
        setSubmitted(true);
        document
          .getElementById("reservation-field-partySize")
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      } else {
        showToast({ message: partySizeError, type: "warning" });
      }
      return;
    }

    setSaving(true);
    try {
      const updated = await updateTableReservation(id, payload);
      setReservation(updated);
      setForm({
        status: updated.status,
        floorPlanDetailId: updated.floorPlanDetailId ?? "",
        partySize: String(updated.partySize ?? ""),
        customerPhone: updated.customerPhone ?? "",
        customerEmail: updated.customerEmail ?? "",
        notes: updated.notes ?? "",
      });
      setStatusLabel(
        RESERVATION_STATUS_LABELS[updated.status as ReservationStatus] ?? ""
      );
      showToast({
        message: t(language, "Reservation updated"),
        type: "success",
      });
    } catch (err: any) {
      showApiError(err, { language, titleFallback: t(language, "Failed to update reservation") });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setNoChangeNotice(false);
  }, [form]);

  const statusEntries = Object.entries(RESERVATION_STATUS_LABELS).map(
    ([value, label]) => ({
      id: value,
      value: Number(value) as ReservationStatus,
      label,
    })
  );

  const { tables, loading: tablesLoading, error: tablesError } =
    useLocationTables(reservation?.locationId ?? "", language);

  /**
   * The assigned table as the picker knows it. When the row points at a table
   * the floor plan no longer returns (deleted, moved to another plan, or a
   * plan that failed to load) the option stays in the list so the field is
   * never silently blank — but it is named, never printed as its id: a
   * `floor_plan_details` UUID tells a host nothing and reads as corruption.
   * The rest of the location's tables are still there to pick from.
   */
  const tableOptions = useMemo(() => {
    if (
      !form.floorPlanDetailId ||
      tables.some((table) => table.id === form.floorPlanDetailId)
    ) {
      return tables;
    }
    return [unresolvedTableOption(language, form.floorPlanDetailId), ...tables];
  }, [tables, form.floorPlanDetailId, language]);

  /**
   * Keep the desktop dropdown's visible text in step with the loaded row:
   * the table list arrives after the reservation, so the label can only be
   * resolved once both are in.
   */
  useEffect(() => {
    if (!form.floorPlanDetailId) {
      setTableLabel("");
      return;
    }
    const match = tableOptions.find(
      (option) => option.id === form.floorPlanDetailId
    );
    setTableLabel(
      match ? match.label : t(language, "Table no longer available")
    );
  }, [form.floorPlanDetailId, tableOptions]);

  const tableHint = tableLoadHint(
    language,
    tablesError,
    tables.length === 0 && !tablesLoading,
  );

  /** Party size is the only free-numeric field left; validated inline on the touch layout. */
  const [submitted, setSubmitted] = useState(false);
  const partySizeError = useMemo(() => {
    if (form.partySize.trim() === "") return undefined;
    const parsed = parseInt(form.partySize, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 500) {
      return t(language, "Party size must be between 1 and 500");
    }
    return undefined;
  }, [form.partySize, language]);

  /**
   * Status changes that end the booking confirm in a sheet naming the guest
   * before they are staged into the form.
   */
  const [pendingStatus, setPendingStatus] = useState<ReservationStatus | null>(
    null
  );

  const handleStatusSegment = (value: string) => {
    const next = Number(value) as ReservationStatus;
    if (next === form.status) return;
    if (
      next === ReservationStatus.Cancelled ||
      next === ReservationStatus.NoShow
    ) {
      setPendingStatus(next);
      return;
    }
    setForm((prev) => ({ ...prev, status: next }));
    setStatusLabel(RESERVATION_STATUS_LABELS[next] ?? "");
  };

  const locationName = reservation
    ? locationsById[reservation.locationId]?.name ?? reservation.locationId
    : "-";

  const sourceLabel = reservation
    ? RESERVATION_SOURCE_LABELS[reservation.source as ReservationSource] ?? "-"
    : "-";

  if (isTouchLayout) {
    /**
     * Touch layout (<1024 — the host shell shows its sidebar from 1024 up,
     * so viewport-fixed chrome stops just below it). The booking the user
     * came to look at is the
     * first thing on screen (a read-only summary card), the editable fields
     * follow, and Save is pinned in a sticky footer. Status is a row of pills
     * — six values, one tap — and the table is chosen from a sheet of the
     * location's floor plan, never typed as a UUID. Tablet (700–1023px) puts the
     * edit fields two-up; the summary stays one column so its label/value
     * pairs keep their reading order.
     */
    const summary: { label: string; value: React.ReactNode }[] = reservation
      ? [
          { label: t(language, "Reservation #"), value: reservation.reservationNumber },
          { label: t(language, "Customer"), value: reservation.customerName },
          { label: t(language, "Reserved from"), value: formatDateTime(reservation.reservedFrom) },
          { label: t(language, "Reserved to"), value: formatDateTime(reservation.reservedTo) },
          { label: t(language, "Location"), value: locationName },
          { label: t(language, "Source"), value: sourceLabel },
          { label: t(language, "Created at"), value: formatDateTime(reservation.createdAt) },
        ]
      : [];

    return (
      <div className="flex flex-col h-full min-h-0" style={{ position: "relative" }}>
        {(loading || saving) && <LoadingV2 />}
        <div
          className="scroll-container"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: `${STICKY_FOOTER_HEIGHT}px`,
          }}
        >
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center font-inter"
            style={{
              gap: "8px",
              height: `${TAP}px`,
              padding: 0,
              border: "none",
              background: "transparent",
              color: TEAL,
              fontSize: "15px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <BackIcon />
            {t(language, "Back")}
          </button>

          {!reservation && !loading ? (
            <p style={{ ...labelStyle, marginTop: "16px" }}>
              {t(language, "Reservation not found")}
            </p>
          ) : reservation ? (
            <>
              <div
                className="flex items-center"
                style={{ gap: "10px", margin: "4px 0 16px", flexWrap: "wrap" }}
              >
                <p
                  className="font-semibold font-inter"
                  style={{ fontSize: "22px", lineHeight: "28px", color: TEXT }}
                >
                  {reservation.customerName}
                </p>
                <TagStatus
                  value={
                    RESERVATION_STATUS_LABELS[reservation.status as ReservationStatus] ?? "-"
                  }
                  label={t(
                    language,
                    RESERVATION_STATUS_LABELS[reservation.status as ReservationStatus] ?? "-",
                  )}
                />
              </div>

              {/* What is booked today — read first, edited below. */}
              <div
                style={{
                  border: `1px solid ${BORDER_SOFT}`,
                  borderRadius: "12px",
                  padding: "4px 14px",
                  marginBottom: "20px",
                }}
              >
                {summary.map((rowItem, index) => (
                  <div
                    key={rowItem.label}
                    className="flex items-center"
                    style={{
                      gap: "12px",
                      minHeight: `${TAP}px`,
                      padding: "8px 0",
                      borderBottom:
                        index === summary.length - 1 ? undefined : `1px solid ${BORDER_SOFT}`,
                    }}
                  >
                    <span
                      className="font-inter"
                      style={{ fontSize: "13px", color: TEXT_DIM, flex: "none", width: "120px" }}
                    >
                      {rowItem.label}
                    </span>
                    <span
                      className="font-inter"
                      style={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: TEXT,
                        flex: 1,
                        minWidth: 0,
                        wordBreak: "break-word",
                      }}
                    >
                      {rowItem.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Only the pill row needs a heading — the sheet variant of
                  StatusChoice carries "Status" inside the field. */}
              {isTablet && (
                <p
                  className="font-inter"
                  style={{ fontSize: "13px", fontWeight: 500, color: TEXT_MUTED, marginBottom: "6px" }}
                >
                  {t(language, "Status")}
                </p>
              )}
              <div style={{ marginBottom: "16px" }}>
                <StatusChoice
                  label={t(language, "Status")}
                  sheetTitle={t(language, "Change status")}
                  options={statusSegments(language)}
                  value={form.status === null ? "" : String(form.status)}
                  onChange={handleStatusSegment}
                  pillsFit={isTablet}
                />
              </div>

              <FormGrid isTablet={isTablet}>
                <div id="reservation-field-floorPlanDetailId">
                  <PickerField
                    language={language}
                    label={t(language, "Table")}
                    sheetTitle={t(language, "Assign a table")}
                    placeholder={t(language, "No table assigned")}
                    options={tableOptions}
                    value={form.floorPlanDetailId}
                    loading={tablesLoading}
                    hint={tableHint}
                    clearLabel={t(language, "No table assigned")}
                    onChange={(tableId) =>
                      setForm((prev) => ({ ...prev, floorPlanDetailId: tableId }))
                    }
                  />
                </div>

                <div id="reservation-field-partySize">
                  <Stepper
                    language={language}
                    label={t(language, "Party size")}
                    value={form.partySize}
                    error={submitted ? partySizeError : undefined}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, partySize: value }))
                    }
                  />
                </div>

                <Field
                  label={t(language, "Customer phone")}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder={t(language, "Optional")}
                  value={form.customerPhone}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, customerPhone: e.target.value }))
                  }
                />

                <Field
                  label={t(language, "Customer email")}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder={t(language, "Optional")}
                  value={form.customerEmail}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, customerEmail: e.target.value }))
                  }
                />

                <FormGridFull isTablet={isTablet}>
                  <div style={{ marginBottom: "16px" }}>
                    <FieldLabel htmlFor="reservation-notes">
                      {t(language, "Notes")}
                    </FieldLabel>
                    <textarea
                      id="reservation-notes"
                      value={form.notes}
                      placeholder={t(language, "Internal notes")}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, notes: e.target.value }))
                      }
                      className="font-inter placeholder:text-placeholder"
                      style={{
                        ...inputStyle,
                        height: "120px",
                        padding: "12px 14px",
                        fontSize: "16px",
                        resize: "vertical",
                        borderColor: "#C9D7D8",
                      }}
                    />
                  </div>
                </FormGridFull>
              </FormGrid>
            </>
          ) : null}
        </div>

        {reservation && noChangeNotice && (
          <p
            role="status"
            className="font-inter"
            style={{
              // Fixed, like the footer it sits on: the scroll container is
              // inset by the host's page padding, so an absolutely positioned
              // notice would float above the footer instead of meeting it.
              position: "fixed",
              left: 0,
              right: 0,
              bottom: `calc(${STICKY_FOOTER_HEIGHT}px + ${TABBAR_OFFSET} + ${SAFE_BOTTOM})`,
              zIndex: 150,
              padding: "8px 16px",
              backgroundColor: "#FFF7E6",
              borderTop: `1px solid ${BORDER_SOFT}`,
              fontSize: "13px",
              color: TEXT_MUTED,
              textAlign: "center",
            }}
          >
            {t(language, "Nothing to save")}
          </p>
        )}

        {reservation && (
          <StickyFooter
            cancelLabel={t(language, "Cancel")}
            onCancel={handleBack}
            primaryLabel={saving ? t(language, "Saving…") : t(language, "Save changes")}
            onPrimary={handleSave}
            primaryDisabled={saving}
          />
        )}

        <ConfirmSheet
          open={pendingStatus !== null && !!reservation}
          title={
            pendingStatus === ReservationStatus.Cancelled
              ? t(language, "Cancel booking")
              : t(language, "Mark as no show")
          }
          message={
            reservation
              ? `${
                  pendingStatus === ReservationStatus.Cancelled
                    ? t(language, "Cancel the booking for")
                    : t(language, "Mark a no show for")
                } ${reservation.customerName} · ${reservation.reservationNumber} (${formatDateTime(
                  reservation.reservedFrom
                )})?`
              : ""
          }
          confirmLabel={
            pendingStatus === ReservationStatus.Cancelled
              ? t(language, "Cancel booking")
              : t(language, "Mark as no show")
          }
          cancelLabel={t(language, "Keep booking")}
          onCancel={() => setPendingStatus(null)}
          onConfirm={() => {
            const next = pendingStatus;
            setPendingStatus(null);
            if (next !== null) {
              setForm((prev) => ({ ...prev, status: next }));
              setStatusLabel(RESERVATION_STATUS_LABELS[next] ?? "");
            }
          }}
        />
      </div>
    );
  }

  return (
    // overflowY:auto so the page scrolls when the form (Notes textarea +
    // save bar) extends below the viewport. The parent layout wrapper has
    // overflow:hidden / fixed-height — without this, content past the fold
    // is clipped.
    <div
      className="h-full relative"
      style={{ paddingBottom: "96px", overflowY: "auto" }}
    >
      {(loading || saving) && <LoadingV2 />}

      <div
        className="flex items-center cursor-pointer"
        style={{ gap: "8px", marginBottom: "16px" }}
        onClick={handleBack}
      >
        <BackIcon />
        <span
          className="font-inter"
          style={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#0F7679",
          }}
        >
          {t(language, "Back")}
        </span>
      </div>

      <p
        className="font-inter"
        style={{
          fontSize: "24px",
          fontWeight: 500,
          color: "#222525",
          marginBottom: "24px",
        }}
      >
        {t(language, "Reservation details")}
      </p>

      {!reservation && !loading ? (
        <p style={labelStyle}>{t(language, "Reservation not found")}</p>
      ) : reservation ? (
        <>
          {/* Read-only summary */}
          <div
            style={{
              border: "1px solid #EBF0F0",
              borderRadius: "12px",
              marginBottom: "32px",
              backgroundColor: "#FFFFFF",
            }}
          >
            <div style={cardRowStyle(false)}>
              <p style={sectionTitleStyle}>
                {t(language, "Booking summary")}
              </p>
            </div>
            <div style={cardRowStyle(false)}>
              <SummaryRow
                label={t(language, "Reservation #")}
                value={reservation.reservationNumber}
              />
              <SummaryRow
                label={t(language, "Customer")}
                value={reservation.customerName}
              />
              <SummaryRow
                label={t(language, "Reserved from")}
                value={formatDateTime(reservation.reservedFrom)}
              />
              <SummaryRow
                label={t(language, "Reserved to")}
                value={formatDateTime(reservation.reservedTo)}
              />
              <SummaryRow
                label={t(language, "Source")}
                value={sourceLabel}
              />
              <SummaryRow
                label={t(language, "Location")}
                value={locationName}
              />
              <SummaryRow
                label={t(language, "Created at")}
                value={formatDateTime(reservation.createdAt)}
                isLast
              />
            </div>
          </div>

          {/* Edit form */}
          <div
            style={{
              border: "1px solid #EBF0F0",
              borderRadius: "12px",
              backgroundColor: "#FFFFFF",
              padding: "24px",
            }}
          >
            <p style={{ ...sectionTitleStyle, marginBottom: "20px" }}>
              {t(language, "Edit reservation")}
            </p>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <FormField
                label={t(language, "Status")}
                isMobile={isMobile}
              >
                {/* DropdownSearch fills its parent's height. Without this
                    48px wrapper it stretches to fill the FormField's
                    flex-column auto-grow and ends up roughly 2× taller
                    than the sibling text inputs (which all have a fixed
                    48px). Same wrapper convention as Reservations.tsx. */}
                <div style={{ height: "48px" }}>
                  <DropdownSearch
                    refKey="status"
                    refs={{ status: statusDropdownRef }}
                    openStates={{ status: statusDropdownOpen }}
                    open={() => setStatusDropdownOpen(true)}
                    close={() => setStatusDropdownOpen(false)}
                    data={statusEntries}
                    labelExtractor={(item) => item.label}
                    value={statusLabel}
                    setValue={setStatusLabel}
                    onSelect={(item) => {
                      setForm((prev) => ({ ...prev, status: item.value }));
                      setStatusLabel(item.label);
                      setStatusDropdownOpen(false);
                    }}
                    placeholder={t(language, "Select status")}
                  />
                </div>
              </FormField>

              {/* The table is chosen by name + covers from the location's
                  floor plan, the same DropdownSearch idiom Status above uses.
                  It replaced a free-text UUID box: nobody can be expected to
                  know a floor_plan_details id, and the field rendered the raw
                  id back at them for an already-assigned booking. */}
              <FormField
                label={t(language, "Table")}
                isMobile={isMobile}
                hint={tableHint}
              >
                <div style={{ display: "flex", gap: "8px" }}>
                  <div style={{ height: "48px", flex: 1, minWidth: 0 }}>
                    <DropdownSearch
                      refKey="table"
                      refs={{ table: tableDropdownRef }}
                      openStates={{ table: tableDropdownOpen }}
                      open={() => setTableDropdownOpen(true)}
                      close={() => setTableDropdownOpen(false)}
                      data={tableOptions}
                      labelExtractor={(item: any) =>
                        item.hint ? `${item.label} · ${item.hint}` : item.label
                      }
                      value={tableLabel}
                      setValue={setTableLabel}
                      onSelect={(item: any) => {
                        setForm((prev) => ({
                          ...prev,
                          floorPlanDetailId: item.id,
                        }));
                        setTableLabel(item.label);
                        setTableDropdownOpen(false);
                      }}
                      loading={tablesLoading}
                      placeholder={t(language, "No table assigned")}
                    />
                  </div>
                  {form.floorPlanDetailId && (
                    <button
                      type="button"
                      style={buttonSecondaryStyle}
                      onClick={() => {
                        setForm((prev) => ({ ...prev, floorPlanDetailId: "" }));
                        setTableLabel("");
                      }}
                    >
                      {t(language, "Clear")}
                    </button>
                  )}
                </div>
              </FormField>

              <FormField label={t(language, "Party size")} isMobile={isMobile}>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={form.partySize}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, partySize: e.target.value }))
                  }
                  style={inputStyle}
                />
              </FormField>

              <FormField
                label={t(language, "Customer phone")}
                isMobile={isMobile}
              >
                <input
                  type="text"
                  value={form.customerPhone}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      customerPhone: e.target.value,
                    }))
                  }
                  style={inputStyle}
                  placeholder={t(language, "Optional")}
                />
              </FormField>

              <FormField
                label={t(language, "Customer email")}
                isMobile={isMobile}
              >
                <input
                  type="email"
                  value={form.customerEmail}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      customerEmail: e.target.value,
                    }))
                  }
                  style={inputStyle}
                  placeholder={t(language, "Optional")}
                />
              </FormField>

              <FormField
                label={t(language, "Notes")}
                isMobile={isMobile}
                fullWidth
              >
                <textarea
                  value={form.notes}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  style={textareaStyle}
                  placeholder={t(language, "Internal notes")}
                />
              </FormField>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                type="button"
                style={buttonSecondaryStyle}
                onClick={handleBack}
                disabled={saving}
              >
                {t(language, "Cancel")}
              </button>
              <button
                type="button"
                style={buttonPrimaryStyle}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? t(language, "Saving…") : t(language, "Save changes")}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

const SummaryRow: React.FC<{
  label: string;
  value: string;
  isLast?: boolean;
}> = ({ label, value, isLast = false }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0",
      borderBottom: isLast ? "none" : "1px solid #EBF0F0",
      gap: "16px",
    }}
  >
    <span style={labelStyle}>{label}</span>
    <span
      className="font-inter"
      style={{
        fontSize: "14px",
        fontWeight: 500,
        color: "#222525",
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value}
    </span>
  </div>
);

const FormField: React.FC<{
  label: string;
  children: React.ReactNode;
  isMobile?: boolean;
  fullWidth?: boolean;
  hint?: string;
}> = ({ label, children, isMobile, fullWidth, hint }) => (
  <div
    style={{
      width: fullWidth || isMobile ? "100%" : "calc(50% - 12px)",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    }}
  >
    <label style={labelStyle}>{label}</label>
    {children}
    {hint && (
      <span
        style={{
          ...labelStyle,
          fontSize: "12px",
          color: "#6A7878",
        }}
      >
        {hint}
      </span>
    )}
  </div>
);

export default TableReservationsDetail;
